import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { apiClient } from '@/api/client';
import { API_USER, API_USER_PROFILES_SWITCH } from '@/api/endpoints';
import { USER_PROFILES_QUERY_KEY, USER_QUERY_KEY } from '@/api/queryKeys';
import {
  hasLogoutStarted,
  resetProfileState,
  setProfileIdentity,
} from '@/auth/session';
import { moveHistoryFloorBelowCurrentPage } from '@/router/backNavigation';
import { ROUTE_HOME } from '@/router/routes';
import type {
  ProfileIdentity,
  SwitchProfileRequest,
  UserInfoResponse,
} from '@/types/user';
import { unwrapAxiosResponse } from '@/utils/axios';
import { ErrorMessage, logRequestError } from '@/utils/errors';

const PROFILE_INDEPENDENT_QUERY_KEYS: string[] = [
  USER_QUERY_KEY[0],
  USER_PROFILES_QUERY_KEY[0],
];

interface UseSwitchProfileResult {
  switchProfile: (identity: ProfileIdentity) => Promise<void>;
  isPending: boolean;
}

export const useSwitchProfile = (): UseSwitchProfileResult => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (identity: ProfileIdentity) =>
      apiClient.post<unknown, unknown, SwitchProfileRequest>(
        API_USER_PROFILES_SWITCH,
        identity,
      ),
  });

  const switchProfile = async (identity: ProfileIdentity): Promise<void> => {
    const isLoggingOut = hasLogoutStarted();

    if (isPending || isLoggingOut) {
      return;
    }

    try {
      await mutateAsync(identity);
      setProfileIdentity(identity);
      moveHistoryFloorBelowCurrentPage();
      navigate(ROUTE_HOME, { replace: true });
      queryClient.removeQueries({
        predicate: (query) =>
          !PROFILE_INDEPENDENT_QUERY_KEYS.includes(query.queryKey[0] as string),
      });
      resetProfileState();

      const currentUser = await apiClient
        .get<UserInfoResponse>(API_USER)
        .then(unwrapAxiosResponse);

      queryClient.setQueryData(USER_QUERY_KEY, currentUser);
    } catch (error) {
      logRequestError(ErrorMessage.SwitchProfile, error);
    }
  };

  return { switchProfile, isPending };
};
